import { useEffect } from 'react'
import { Alert, Button, Form, Input } from 'antd'
import {
  attachExistingPayment,
  childProductSubscription,
  clientSignup,
  login,
} from '../../api'
import { interpretLoginResponse } from '../../authFlow'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { normalizeSignupPlatformId, toDatabaseName } from '../utils'
import { useSignup } from '../SignupContext'

export default function LoginStepForm() {
  const {
    loginForm,
    customerForm,
    setCustomerForm,
    setClientSignupCompleted,
    setPlatformId,
    setStep,
    runAction,
    establishSignupSession,
    productSelection,
    pricingForm,
    nocc,
    loading,
    hideBackButton,
    setInfo,
  } = useSignup()

  useEffect(() => {
    if (customerForm?.email) {
      loginForm.setFieldsValue({ email: customerForm.email, password: '' })
    }
  }, [customerForm?.email, loginForm])

  const handleNext = async () => {
    await runAction(async () => {
      const values = await loginForm.validateFields()
      if (!customerForm || !productSelection?.product) throw new Error('Missing signup context')

      if (values.email.toLowerCase() !== customerForm.email.toLowerCase()) {
        throw new Error('The email does not match the one from Client Info.')
      }

      const loginResponse = await login({ userName: values.email, password: values.password, isUpdateEmail: false })
      const loginDecision = interpretLoginResponse(loginResponse)
      if (loginDecision.kind === 'verification-sent' || loginDecision.kind === 'unconfirmed') {
        setInfo(loginDecision.message)
        return
      }

      if (loginDecision.kind !== 'success') {
        throw new Error(loginDecision.message)
      }

      const { idToken, refreshToken, expiry } = loginResponse
      if (!idToken || !refreshToken || expiry == null) {
        throw new Error('Invalid email or password.')
      }

      await establishSignupSession(
        loginResponse.email ?? values.email,
        {
          idToken,
          refreshToken,
          expiry,
        },
        { hydrateSession: true }
      )

      const signupPayload = {
        clientForm: {
          ...customerForm,
          databaseName: toDatabaseName(customerForm.companyName),
        },
        platformType: productSelection.product,
        nocc,
      }

      const signupResult = await clientSignup(signupPayload)
      if (signupResult.error || !signupResult.client?.clientId) {
        throw new Error(signupResult.error || 'Unable to create child client')
      }

      setPlatformId(normalizeSignupPlatformId(signupResult.platformId))
      setClientSignupCompleted(true)
      setCustomerForm({ ...customerForm, clientId: signupResult.client.clientId, userId: signupResult.userId })

      if (!nocc) {
        const existingPayment = await attachExistingPayment({ ClientId: signupResult.client.clientId })
        if (!existingPayment?.id) {
          throw new Error('No existing payment methods found. Please add a payment method to continue.')
        }

        const subscribed = await childProductSubscription({
          productSubscription: {
            CustomerID: '',
            PromoCode: pricingForm.couponCode,
            Users: pricingForm.activeUsers,
            PaymentMethodId: existingPayment.id,
            CustomNumOfUsers: pricingForm.customUsers,
            ClientId: signupResult.client.clientId,
          },
          platformType: productSelection.product,
        })

        if (subscribed === false) {
          throw new Error('Parent client setup is incomplete. Please complete parent client setup first.')
        }
      }

      setStep('importData')
    }, false)
  }

  const handleBack = () => setStep('pricing')

  return (
    <>
      <Form form={loginForm} layout="vertical" initialValues={{ email: customerForm?.email || '' }}>
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="By logging in, you authorize this transaction and consent to charge your saved payment method on file."
        />
        <div className="signup-grid">
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </div>
      </Form>
      <div className="signup-step-actions">
        {!hideBackButton && <Button onClick={handleBack} disabled={loading}>Back</Button>}
        <Button type="primary" loading={loading} disabled={loading} className={AUTH_PRIMARY_BUTTON_CLASS} onClick={() => void handleNext()}>
          Next
        </Button>
      </div>
    </>
  )
}
